import { handleAxiosError } from '@/api/helpers';
import { client } from '@/client';
import {
  EmailProvider,
  MailConnectorIPAddresses,
  SendTestEmailFormValues,
  WhitelistingDomain,
  WhitelistingIPAddresses,
  WhitelistingSimulationUrl,
} from '@/client/campaigns';
import {
  MailSystemDetectionResult,
  MailSystemType,
  SendingProfileType,
} from '@/client/sender-profiles';
import { LoadingStatuses } from '@/common/constants';
import {
  MAIL_CONNECTOR_GUIDE,
  WHITELISTING_GUIDE,
} from '@/common/constants/external-urls';
import VideoGuideConnector from '@/components/campaigns/simulations/assets/Whitelisting-Connector.mp4';
import VideoGuideGoogle from '@/components/campaigns/simulations/assets/Whitelisting-Google.mp4';
import VideoGuideMicrosoft from '@/components/campaigns/simulations/assets/Whitelisting-Microsoft-Defender.mp4';
import { useNotifications } from '@/hooks/notifications.hook';
import {
  useSendWhitelistingTestEmail,
  useSetupMailConnector,
} from '@/hooks/query/accounts.hooks';
import { useDetectMailSystem } from '@/hooks/query/sender-profiles.hooks';
import { useAppDispatch, useAppSelector } from '@/hooks/store';
import { useToast } from '@/hooks/useToast';
import ErrorIcon from '@/images/si_error-line.svg';
import {
  selectCurrentAccount,
  setCurrentAccount,
} from '@/store/features/account';
import { selectCurrentUser } from '@/store/features/users';
import { AppButton, FlexContainer } from '@/ui';
import { CopyIcon } from '@/utils/helpers/icons.helper';
import { AxiosError } from 'axios';
import { Dialog } from 'primereact/dialog';
import { Dropdown } from 'primereact/dropdown';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tooltip } from 'primereact/tooltip';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import styled, { css } from 'styled-components';
import { SendTestEmailModal } from '../../SendTestEmailModal';

const subtleLinkStyles = css`
  color: var(--black-main);
  text-decoration: none;
  border-bottom: 2px solid var(--gray-border-light);
  padding-bottom: var(--xxsmall-padding);
  cursor: pointer;
  font-weight: 500;

  &:hover {
    color: var(--green-color);
  }
`;

const StyledDialog = styled(Dialog)`
  width: 700px;
  max-height: 800px;
  height: 100vh;

  .p-dialog-title {
    font-size: 20px !important;
    font-weight: 600 !important;
    color: var(--black-main) !important;
  }

  .p-dialog-header {
    padding: var(--default-padding) var(--default-padding) 0
      var(--default-padding) !important;
  }

  .p-dialog-content {
    padding: 0 var(--default-padding) var(--default-padding)
      var(--default-padding) !important;
    overflow-y: auto !important;
  }

  &.detecting-state {
    .p-dialog-header {
      display: none;
    }

    .p-dialog-content {
      padding: 0 !important;
      height: 100%;

      > div {
        height: 100%;
      }
    }
  }

  &.error-state {
    .p-dialog-title {
      display: none;
    }

    .p-dialog-content {
      padding: 0 !important;
    }
  }
`;

const ModalContent = styled.div`
  padding: 0;

  hr {
    height: 1px;
    background-color: var(--gray-moderate);
    border: none;
    margin: var(--default-padding) 0;
  }
`;

const ModalDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: 1.5;
  margin: var(--xxsmall-padding) 0 var(--default-padding) 0;

  a {
    ${subtleLinkStyles}
    padding-bottom: var(--xxxsmall-padding);
  }
`;

const VideoSection = styled.div`
  margin-bottom: var(--default-padding);
`;

const VideoPlayer = styled(FlexContainer)`
  width: 100%;
  border-radius: var(--box-border-radius);
  position: relative;
  cursor: pointer;
  overflow: hidden;

  video {
    width: 100%;
  }
`;

const PlayButton = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 38px;
  height: 38px;
  border-radius: var(--box-border-radius);
  background: var(--green-color);
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  &::before {
    content: '';
    width: 0;
    height: 0;
    border-left: var(--medium-padding) solid var(--white-main);
    border-top: var(--xsmall-padding) solid transparent;
    border-bottom: var(--xsmall-padding) solid transparent;
    margin-left: var(--xxsmall-padding);
  }
`;

const WrittenGuideSection = styled(FlexContainer)`
  margin: var(--medium-big-padding) 0 var(--default-padding) 0;
  font-size: var(--small-font-size);
`;

const WrittenGuideText = styled.p`
  color: var(--black-main);
  font-size: var(--medium-font-size);
  font-weight: 600;
  margin: 0 0 var(--xsmall-padding) 0;
`;

const WrittenGuideLink = styled.a`
  ${subtleLinkStyles}
  line-height: var(--small-line-height);
  font-size: var(--small-font-size);
`;

const RequiredDetailsSection = styled.div`
  margin-top: var(--default-padding);
`;

const RequiredDetailsTitle = styled.h3`
  color: var(--black-main);
  font-size: var(--medium-font-size);
  font-weight: 600;
  margin: 0 0 var(--small-padding) 0;
`;

const DetailsContainer = styled.div`
  display: flex;
  gap: var(--xsmall-padding);
  width: 100%;
  align-items: stretch;

  > div {
    flex: 1;
  }

  > .details-column > div {
    flex: 1;
  }
`;

const DetailBox = styled.div<{ variant?: 'light' | 'dark' | 'darker' }>`
  display: flex;
  gap: var(--xxsmall-padding);
  width: 100%;
  background: ${({ variant }) =>
    variant === 'light'
      ? 'var(--green-opacity-lighter)'
      : variant === 'dark'
      ? 'var(--green-color)'
      : 'var(--green-chart-dark)'};
  border-radius: var(--box-medium-border-radius);
  padding: var(--semi-medium-padding) var(--medium-padding);
  position: relative;
  color: ${({ variant }) =>
    variant === 'light' ? 'var(--black-main)' : 'white'};

  svg path {
    fill: ${({ variant }) =>
      variant === 'light' ? 'var(--green-color)' : 'white'};
  }
`;

const CopyIconButton = styled.button`
  position: absolute;
  top: 0;
  right: 0;

  background: none;
  border: none;
  cursor: pointer;
  color: inherit;
  opacity: 0.8;
  transition: opacity 0.2s ease;
  padding: var(--xsmall-padding);
  border-radius: var(--xxsmall-border-radius);
  align-self: flex-start;

  &:hover {
    opacity: 1;
  }

  i {
    font-size: var(--semi-big-font-size);
  }
`;

const DetailLabel = styled.div`
  font-weight: 600;
  font-size: var(--small-font-size);
`;

const DetailValue = styled.div`
  font-size: var(--small-font-size);
  line-height: 1.5;
  font-weight: 500;
`;

const IPAddressesList = styled.div`
  display: flex;
  flex-direction: column;
  font-size: var(--small-font-size);
  justify-content: flex-start;
  line-height: 140%;
`;

const IPAddress = styled.div`
  font-size: var(--small-font-size);
  font-weight: 500;
  line-height: 140%;
`;

const SendTestEmailSection = styled.div`
  background: var(--green-opacity-lighter);
  border-radius: var(--box-medium-border-radius);
  padding: var(--default-padding);
  margin-top: var(--default-padding);
`;

const SectionTitle = styled.p`
  color: var(--black-main);
  font-size: var(--medium-font-size);
  font-weight: 600;
  margin: 0;
`;

const SectionDescription = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  line-height: 1.5;
  margin: 0;
`;

const HelpSection = styled.div`
  padding-top: var(--medium-padding);
`;

const HelpTitle = styled.p`
  color: var(--black-main);
  font-size: var(--medium-font-size);
  font-weight: 600;
  margin: 0 0 var(--xxsmall-padding) 0;
`;

const HelpText = styled.p`
  color: var(--black-main);
  font-size: var(--small-font-size);
  margin: 0;
`;

const VerifySection = styled.div`
  background: white;
  border-radius: var(--box-medium-border-radius);
  padding-top: 0;
  padding-bottom: var(--default-padding);
`;

const ToggleContainer = styled.div`
  display: flex;
  gap: var(--medium-padding);
  margin: var(--default-padding) 0;
`;

const ConnectorVerifySection = styled(FlexContainer)`
  margin: var(--default-padding) 0 var(--semi-big-padding) 0;
`;

const ToggleItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--small-padding);
`;

const ToggleLabel = styled.label`
  color: var(--black-main);
  font-size: var(--small-font-size);
  font-weight: 500;
  cursor: pointer;
`;

const ToggleSwitch = styled.div<{ isChecked: boolean }>`
  position: relative;
  width: 44px;
  height: 24px;
  background: ${({ isChecked }) =>
    isChecked ? 'var(--green-color)' : 'var(--gray-moderate)'};
  border-radius: var(--box-medium-border-radius);
  cursor: pointer;
  transition: background-color 0.2s ease;

  &::after {
    content: '';
    position: absolute;
    top: var(--xxxsmall-padding);
    left: ${({ isChecked }) =>
      isChecked ? '22px' : 'var(--xxxsmall-padding)'};
    width: 20px;
    height: 20px;
    background: var(--white-main);
    border-radius: 50%;
    transition: left 0.2s ease;
  }
`;

const LoadingSection = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  padding: 120px var(--big-padding) 140px;
  flex-direction: column;
  gap: var(--default-padding);
  min-height: 400px;
  height: 100%;

  .p-progress-spinner-circle {
    stroke: var(--red-main) !important;
  }

  p {
    font-size: 24px;
    color: var(--black-main);
    margin: 0;
    font-weight: 700;
  }
`;

const ErrorSection = styled(FlexContainer)`
  justify-content: center;
  align-items: center;
  padding: 120px var(--big-padding) 140px;
  flex-direction: column;
  gap: var(--default-padding);
  min-height: 400px;
  text-align: center;
`;

const ErrorIconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: var(--default-padding);
`;

const ErrorTitle = styled.h3`
  font-size: 24px;
  font-weight: 700;
  color: var(--black-main);
  margin: 0;
`;

const ErrorDescription = styled.p`
  font-size: 16px;
  color: var(--black-main);
  margin: var(--medium-padding) 0 var(--big-padding) 0;
  line-height: 1.5;

  a {
    color: var(--black-main);
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }
`;

const ErrorButtonsContainer = styled(FlexContainer)`
  gap: var(--default-padding);
  flex-direction: row;
`;

const ManualSelectionSection = styled.div`
  padding: var(--default-padding) 0;
  display: flex;
  flex-direction: column;
  gap: var(--default-padding);

  .p-dropdown {
    width: 100%;
  }
`;

const ConnectorSetupSection = styled.div`
  padding: var(--xxxsmall-padding) 0;
`;

const DomainTableSection = styled.div`
  display: flex;
  flex-direction: column;
  background: var(--green-opacity-lighter);
  padding: var(--default-padding);
  border-radius: var(--box-border-radius);
  margin-top: var(--default-padding);
`;

const DomainRow = styled(FlexContainer)`
  margin-bottom: var(--medium-padding);
`;

const DomainLabel = styled.span`
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
`;

const FqdnInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  border: 1px solid
    ${({ hasError }) => (hasError ? 'var(--red-main)' : 'transparent')};
  border-radius: var(--default-border-radius);
  min-height: var(--default-input-height);
  padding: var(--semi-medium-padding) var(--medium-padding);
  font-size: var(--small-font-size);
  font-weight: 500;
  color: var(--black-main);
  background: var(--white-main);
  outline: none;

  &:focus {
    border-color: var(--green-color);
  }
`;

type WhitelistingModalProps = {
  isOpen: boolean;
  onClose: () => void;
  provider?: EmailProvider;
  accountId: string;
  sendingProfileType?: SendingProfileType;
};
type DetectionStep =
  | 'detecting'
  | 'manual'
  | 'connector-setup'
  | 'wizard'
  | 'error';

const FQDN_REGEX =
  /^(?:[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?\.)+[a-zA-Z]{2,}$/;

const validateFqdn = (value: string): boolean => FQDN_REGEX.test(value.trim());

const guessTransportFqdn = (domain: string): string =>
  `${domain.replace(/\./g, '-')}.mail.protection.outlook.com`;

export const WhitelistingModal: React.FC<WhitelistingModalProps> = ({
  isOpen,
  onClose,
  provider,
  accountId,
  sendingProfileType,
}) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const contentRef = useRef<HTMLDivElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [whitelistingCompleted, setWhitelistingCompleted] = useState(false);
  const [testEmailReceived, setTestEmailReceived] = useState(false);

  const currentUser = useAppSelector(selectCurrentUser);
  const currentAccount = useAppSelector(selectCurrentAccount);
  const [showSendTestEmail, setShowSendTestEmail] = useState<boolean>(false);

  const getInitialDetectionStep = (): DetectionStep => {
    if (provider) return 'wizard';
    return 'detecting';
  };

  const [detectionStep, setDetectionStep] = useState<DetectionStep>(
    getInitialDetectionStep(),
  );
  const [derivedProvider, setDerivedProvider] = useState<EmailProvider | null>(
    provider || null,
  );
  const detectMutation = useDetectMailSystem();

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast?.success('', t('clipboard.copied'));
  };

  const setupMailConnector = useSetupMailConnector();
  const [connectorEntries, setConnectorEntries] = useState<
    { domain: string; fqdn: string; fqdnError: boolean }[]
  >([]);
  const [connectorConfigToggle, setConnectorConfigToggle] = useState(false);
  const [isConnectorMode, setIsConnectorMode] = useState(false);

  const sendWhitelistingTestEmail = useSendWhitelistingTestEmail();
  const {
    lastMessage: testMailLastMessage,
    setNotificationParam: setJobId,
    notificationParam: jobId,
  } = useNotifications(client.senderProfiles.senderProfilesJobNotifyUrl);

  const initConnectorEntries = async (result: MailSystemDetectionResult) => {
    let savedEntries: { domain: string; fqdn: string }[] = [];
    try {
      savedEntries = await client.accounts.getTransportMapEntries(accountId);
    } catch (e) {
      toast?.error(
        t('toast.error'),
        t('phishing.simulations.whitelisting.connector.fqdnFetchFailed'),
      );
    }
    setConnectorEntries(
      result.domains.map((domain) => {
        const saved = savedEntries.find((e) => e.domain === domain);
        return {
          domain,
          fqdn: saved ? saved.fqdn : guessTransportFqdn(domain),
          fqdnError: false,
        };
      }),
    );
    setConnectorConfigToggle(false);
  };

  const handleConnectorFqdnChange = (index: number, value: string) => {
    setConnectorEntries((prev) =>
      prev.map((entry, i) =>
        i === index
          ? { ...entry, fqdn: value, fqdnError: !validateFqdn(value) }
          : entry,
      ),
    );
  };

  const isConnectorNextEnabled =
    connectorConfigToggle &&
    connectorEntries.length > 0 &&
    connectorEntries.every((e) => e.fqdn.trim() && validateFqdn(e.fqdn));

  const handleConnectorNextStep = async () => {
    try {
      await setupMailConnector.setup({
        accountId,
        entries: connectorEntries.map(({ domain, fqdn }) => ({
          domain,
          fqdn: fqdn.trim(),
        })),
      });
      if (currentAccount?.id === accountId) {
        dispatch(
          setCurrentAccount({
            ...currentAccount,
            sendingProfileType: SendingProfileType.CONNECTOR,
          }),
        );
      }
      setIsConnectorMode(true);
      setDerivedProvider(EmailProvider.MICROSOFT);
      setDetectionStep('wizard');
    } catch (e) {
      toast?.error(
        t('toast.error'),
        t('phishing.simulations.whitelisting.connector.setupError'),
      );
    }
  };

  const handleDetectionSuccess = (result: MailSystemDetectionResult) => {
    if (result.mailSystem === MailSystemType.THIRD_PARTY_GATEWAY) {
      initConnectorEntries(result);
      setDetectionStep('connector-setup');
    } else if (result.mailSystem === MailSystemType.MICROSOFT_365) {
      setDerivedProvider(EmailProvider.MICROSOFT);
      setDetectionStep('wizard');
    } else if (result.mailSystem === MailSystemType.GOOGLE_WORKSPACE) {
      setDerivedProvider(EmailProvider.GOOGLE);
      setDetectionStep('wizard');
    } else {
      setDetectionStep('manual');
    }
  };

  const startDetection = () => {
    if (!accountId) return;

    setDetectionStep('detecting');

    detectMutation.mutate(accountId, {
      onSuccess: handleDetectionSuccess,
      onError: () => {
        setDetectionStep('error');
      },
    });
  };

  useEffect(() => {
    if (!isOpen) {
      return;
    }

    setConnectorEntries([]);
    setConnectorConfigToggle(false);
    setIsConnectorMode(
      sendingProfileType !== undefined
        ? sendingProfileType === SendingProfileType.CONNECTOR
        : currentAccount?.id === accountId
        ? currentAccount?.sendingProfileType === SendingProfileType.CONNECTOR
        : false,
    );
    setDerivedProvider(null);
    setWhitelistingCompleted(false);
    setTestEmailReceived(false);
    setJobId(undefined);
    setShowSendTestEmail(false);

    if (provider) {
      setDerivedProvider(provider);
      setDetectionStep('wizard');
      return;
    }

    if (accountId) {
      startDetection();
    } else {
      setDetectionStep('manual');
    }
  }, [isOpen, accountId, provider, sendingProfileType]);

  useEffect(() => {
    const scrollable = contentRef.current?.closest('.p-dialog-content');
    if (scrollable) {
      scrollable.scrollTop = 0;
    }
  }, [detectionStep]);

  useEffect(() => {
    if (!jobId) return;
    handleNotifyEmailSent(testMailLastMessage);
  }, [jobId, testMailLastMessage]);

  const handleNotifyEmailSent = (messages: any) => {
    if (
      !messages?.data?.event ||
      ![
        'sending-profile.email-sent',
        'sending-profile.email-not-sent',
      ].includes(messages?.data?.event)
    )
      return;

    if (messages?.data?.event === 'sending-profile.email-sent')
      toast?.success(t('toast.success'), t('campaign.testEmail.sent'), 5000);
    if (messages?.data?.event === 'sending-profile.email-not-sent')
      toast?.info(t('toast.info'), t('campaign.testEmail.notSent'), 5000);

    setJobId(undefined);
  };

  const handleSendTestEmail = async (data: SendTestEmailFormValues) => {
    if (!accountId) return;

    const email =
      data.emailType === 'custom' ? data.email : (data.admin?.email as string);
    const firstName =
      data.emailType === 'custom' ? data.firstName : data.admin?.firstName;
    const lastName =
      data.emailType === 'custom' ? data.lastName : data.admin?.lastName;

    if (!email) return;
    try {
      const response = await sendWhitelistingTestEmail.send({
        accountId,
        recipientEmail: email,
        recipientName: [firstName, lastName].filter(Boolean).join(' '),
        sendingProfileType: isConnectorMode
          ? SendingProfileType.CONNECTOR
          : SendingProfileType.HEYSENDER,
      });

      setJobId(response.jobId);
    } catch (e) {
      handleAxiosError(e as Error | AxiosError, toast);
    }

    setShowSendTestEmail(false);
  };

  const handleConfirmWhitelisting = async () => {
    if (whitelistingCompleted && testEmailReceived && accountId) {
      try {
        await client.senderProfiles.whitelistAccountSendingProfiles(accountId);
        toast?.success(
          t('toast.success'),
          t('phishing.simulations.senderProfiles.whitelisting.confirmed'),
        );

        if (currentAccount?.id === accountId) {
          const accountData = await client.accounts.getAccount(accountId);
          dispatch(setCurrentAccount(accountData));
        }
        queryClient.invalidateQueries(['account', accountId]);
        onClose();
      } catch (e) {
        handleAxiosError(e as Error | AxiosError, toast);
      }
    }
  };

  const toggleWhitelisting = () => {
    setWhitelistingCompleted(!whitelistingCompleted);
  };

  const toggleTestEmail = () => {
    setTestEmailReceived(!testEmailReceived);
  };

  const getProviderTitle = () => {
    if (detectionStep === 'connector-setup') {
      return t('phishing.simulations.whitelisting.connector.title');
    }
    const activeProvider = derivedProvider || provider;
    return activeProvider === EmailProvider.MICROSOFT
      ? t('phishing.simulations.senderProfiles.whitelisting.microsoft.title')
      : t('phishing.simulations.senderProfiles.whitelisting.google.title');
  };

  const renderRequiredDetails = () => {
    const activeProvider = derivedProvider || provider;
    if (activeProvider === EmailProvider.MICROSOFT) {
      return (
        <DetailsContainer>
          <FlexContainer direction="column" gap={6} className="details-column">
            <DetailBox variant="light">
              <FlexContainer justify="flex-start" gap={6}>
                <DetailLabel>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.domain.label',
                  )}
                </DetailLabel>
                <DetailValue>{WhitelistingDomain}</DetailValue>
              </FlexContainer>
              <CopyIconButton
                onClick={() => handleCopyToClipboard(WhitelistingDomain)}
              >
                <CopyIcon />
              </CopyIconButton>
            </DetailBox>

            <DetailBox variant="dark">
              <FlexContainer justify="flex-start" gap={6}>
                <DetailLabel>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.simulationUrl.label',
                  )}
                </DetailLabel>
                <DetailValue>{WhitelistingSimulationUrl}</DetailValue>
              </FlexContainer>
              <CopyIconButton
                onClick={() => handleCopyToClipboard(WhitelistingSimulationUrl)}
              >
                <CopyIcon />
              </CopyIconButton>
            </DetailBox>
          </FlexContainer>
          <DetailBox variant="darker">
            <FlexContainer justify="space-between" align="flex-start" gap={6}>
              <IPAddressesList>
                <DetailLabel>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.ipAddresses.label',
                  )}
                </DetailLabel>
                {(isConnectorMode
                  ? MailConnectorIPAddresses
                  : WhitelistingIPAddresses
                ).map((ip) => (
                  <IPAddress key={ip}>{ip}</IPAddress>
                ))}
              </IPAddressesList>
              <CopyIconButton
                onClick={() =>
                  handleCopyToClipboard(
                    (isConnectorMode
                      ? MailConnectorIPAddresses
                      : WhitelistingIPAddresses
                    ).join('\n'),
                  )
                }
              >
                <CopyIcon />
              </CopyIconButton>
            </FlexContainer>
          </DetailBox>
        </DetailsContainer>
      );
    } else {
      return (
        <DetailsContainer>
          <DetailBox variant="darker">
            <IPAddressesList>
              <DetailLabel>
                {t(
                  'phishing.simulations.senderProfiles.whitelisting.ipAddresses.label',
                )}
              </DetailLabel>
              {(isConnectorMode
                ? MailConnectorIPAddresses
                : WhitelistingIPAddresses
              ).map((ip) => (
                <IPAddress key={ip}>{ip}</IPAddress>
              ))}
            </IPAddressesList>
            <CopyIconButton
              onClick={() =>
                handleCopyToClipboard(
                  (isConnectorMode
                    ? MailConnectorIPAddresses
                    : WhitelistingIPAddresses
                  ).join('\n'),
                )
              }
            >
              <CopyIcon />
            </CopyIconButton>
          </DetailBox>
        </DetailsContainer>
      );
    }
  };

  return (
    <>
      <StyledDialog
        visible={isOpen}
        onHide={onClose}
        header={
          detectionStep === 'detecting' || detectionStep === 'error'
            ? ''
            : getProviderTitle()
        }
        modal
        draggable={false}
        resizable={false}
        className={`v2 ${
          detectionStep === 'detecting' ? 'detecting-state' : ''
        } ${detectionStep === 'error' ? 'error-state' : ''}`}
        closable={detectionStep !== 'detecting'}
        closeOnEscape={detectionStep !== 'detecting'}
      >
        <ModalContent ref={contentRef}>
          {detectionStep === 'detecting' && (
            <LoadingSection>
              <ProgressSpinner
                style={{ width: '90px', height: '90px' }}
                strokeWidth="8"
              />
              <p>
                {t('phishing.simulations.whitelisting.checkingMailSystems')}
              </p>
            </LoadingSection>
          )}

          {detectionStep === 'error' && (
            <ErrorSection>
              <ErrorIconWrapper>
                <img src={ErrorIcon} alt="Error" />
              </ErrorIconWrapper>
              <ErrorTitle>
                {t('phishing.simulations.whitelisting.detectionError.title')}
              </ErrorTitle>
              <ErrorDescription>
                {t(
                  'phishing.simulations.whitelisting.detectionError.description',
                )}
                <br />
                <a href="mailto:support@cyberpilot.io">support@cyberpilot.io</a>
                {' | '}
                <a href="tel:+4567262626">+45 67 26 26 26</a>
              </ErrorDescription>
              <ErrorButtonsContainer>
                <AppButton
                  label={t(
                    'phishing.simulations.whitelisting.detectionError.tryAgain',
                  )}
                  onClick={startDetection}
                />
                <AppButton
                  label={t(
                    'phishing.simulations.whitelisting.detectionError.contactSupport',
                  )}
                  onClick={() => {
                    window.open('mailto:support@cyberpilot.io', '_blank');
                  }}
                  type="outlined"
                />
              </ErrorButtonsContainer>
            </ErrorSection>
          )}

          {detectionStep === 'connector-setup' && (
            <ConnectorSetupSection>
              <ModalDescription>
                {t('phishing.simulations.whitelisting.connector.description')}
              </ModalDescription>

              <VideoSection>
                <VideoPlayer>
                  <video
                    ref={videoRef}
                    src={VideoGuideConnector}
                    controls
                    onPlay={() => setIsVideoPlaying(true)}
                    onPause={() => setIsVideoPlaying(false)}
                  />
                  {!isVideoPlaying && (
                    <PlayButton
                      className="play-button"
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.play();
                          setIsVideoPlaying(true);
                        }
                      }}
                    />
                  )}
                </VideoPlayer>
              </VideoSection>

              <WrittenGuideSection justify="space-between">
                <WrittenGuideText>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.writtenGuide.title',
                  )}
                </WrittenGuideText>
                <WrittenGuideLink
                  href={MAIL_CONNECTOR_GUIDE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.writtenGuide.link',
                  )}
                </WrittenGuideLink>
              </WrittenGuideSection>

              <hr />

              <RequiredDetailsSection>
                <RequiredDetailsTitle>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.requiredDetails.title',
                  )}
                </RequiredDetailsTitle>
                <DetailBox variant="darker">
                  <FlexContainer
                    justify="space-between"
                    align="flex-start"
                    gap={6}
                  >
                    <IPAddressesList>
                      <DetailLabel>
                        {t(
                          'phishing.simulations.senderProfiles.whitelisting.ipAddresses.label',
                        )}
                      </DetailLabel>
                      {MailConnectorIPAddresses.map((ip) => (
                        <IPAddress key={ip}>{ip}</IPAddress>
                      ))}
                    </IPAddressesList>
                    <CopyIconButton
                      onClick={() =>
                        handleCopyToClipboard(
                          MailConnectorIPAddresses.join('\n'),
                        )
                      }
                    >
                      <CopyIcon />
                    </CopyIconButton>
                  </FlexContainer>
                </DetailBox>
              </RequiredDetailsSection>

              <DomainTableSection>
                <RequiredDetailsTitle>
                  {t(
                    'phishing.simulations.whitelisting.connector.emailDomains',
                  )}
                </RequiredDetailsTitle>
                <ModalDescription
                  dangerouslySetInnerHTML={{
                    __html: t(
                      'phishing.simulations.whitelisting.connector.emailDomainsDescription',
                    ),
                  }}
                />
                {connectorEntries.map((entry, index) => (
                  <DomainRow
                    key={entry.domain}
                    direction="column"
                    gap="var(--xxsmall-padding)"
                    justify="flex-start"
                    align="flex-start"
                  >
                    <DomainLabel>
                      {t(
                        'phishing.simulations.whitelisting.connector.domainLabel',
                        { domain: entry.domain },
                      )}
                    </DomainLabel>
                    <FqdnInput
                      aria-label={t(
                        'phishing.simulations.whitelisting.connector.domainLabel',
                        { domain: entry.domain },
                      )}
                      value={entry.fqdn}
                      hasError={entry.fqdnError}
                      onChange={(e) =>
                        handleConnectorFqdnChange(index, e.target.value)
                      }
                      placeholder={t(
                        'phishing.simulations.whitelisting.connector.fqdnExamplePlaceholder',
                        { fqdn: guessTransportFqdn(entry.domain) },
                      )}
                    />
                  </DomainRow>
                ))}
              </DomainTableSection>

              <hr />

              <ConnectorVerifySection
                justify="flex-start"
                gap="var(--medium-padding)"
              >
                <SectionTitle>
                  {t(
                    'phishing.simulations.whitelisting.connector.verifyToggle',
                  )}
                </SectionTitle>
                <ToggleItem>
                  <ToggleSwitch
                    isChecked={connectorConfigToggle}
                    onClick={() => setConnectorConfigToggle((v) => !v)}
                    role="switch"
                    aria-checked={connectorConfigToggle}
                    tabIndex={0}
                    onKeyDown={(e) => {
                      if (e.key === ' ' || e.key === 'Enter') {
                        e.preventDefault();
                        setConnectorConfigToggle((v) => !v);
                      }
                    }}
                  />
                  <ToggleLabel
                    onClick={() => setConnectorConfigToggle((v) => !v)}
                  >
                    {t(
                      'phishing.simulations.whitelisting.connector.configCompleted',
                    )}
                  </ToggleLabel>
                </ToggleItem>
              </ConnectorVerifySection>

              <AppButton
                label={t(
                  'phishing.simulations.whitelisting.connector.nextStep',
                )}
                onClick={handleConnectorNextStep}
                isDisabled={!isConnectorNextEnabled}
                state={
                  setupMailConnector.isLoading
                    ? LoadingStatuses.LOADING
                    : LoadingStatuses.IDLE
                }
              />
            </ConnectorSetupSection>
          )}

          {detectionStep === 'manual' && (
            <ManualSelectionSection>
              <ModalDescription>
                {t('phishing.simulations.whitelisting.selectProvider')}
              </ModalDescription>
              <Dropdown
                value={derivedProvider}
                options={[
                  {
                    label: 'Microsoft 365',
                    value: EmailProvider.MICROSOFT,
                  },
                  {
                    label: 'Google Workspace',
                    value: EmailProvider.GOOGLE,
                  },
                ]}
                onChange={(e) => setDerivedProvider(e.value)}
                placeholder={t(
                  'phishing.simulations.whitelisting.selectProviderPlaceholder',
                )}
              />
              <AppButton
                label={t('phishing.simulations.whitelisting.continue')}
                onClick={() => setDetectionStep('wizard')}
                isDisabled={!derivedProvider}
              />
            </ManualSelectionSection>
          )}

          {detectionStep === 'wizard' && (
            <>
              <ModalDescription>
                {t(
                  'phishing.simulations.senderProfiles.whitelisting.description',
                )}
              </ModalDescription>

              <VideoSection>
                <VideoPlayer>
                  <video
                    ref={videoRef}
                    src={
                      (derivedProvider || provider) === EmailProvider.MICROSOFT
                        ? VideoGuideMicrosoft
                        : VideoGuideGoogle
                    }
                    controls
                    onPlay={() => {
                      setIsVideoPlaying(true);
                    }}
                    onPause={() => {
                      setIsVideoPlaying(false);
                    }}
                  />
                  {!isVideoPlaying && (
                    <PlayButton
                      className="play-button"
                      onClick={() => {
                        if (videoRef.current) {
                          videoRef.current.play();
                          setIsVideoPlaying(true);
                        }
                      }}
                    />
                  )}
                </VideoPlayer>
              </VideoSection>

              <WrittenGuideSection justify="space-between">
                <WrittenGuideText>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.writtenGuide.title',
                  )}
                </WrittenGuideText>
                <WrittenGuideLink
                  href={WHITELISTING_GUIDE}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.writtenGuide.link',
                  )}
                </WrittenGuideLink>
              </WrittenGuideSection>

              <hr />

              <RequiredDetailsSection>
                <RequiredDetailsTitle>
                  {t(
                    'phishing.simulations.senderProfiles.whitelisting.requiredDetails.title',
                  )}
                </RequiredDetailsTitle>
                {renderRequiredDetails()}
              </RequiredDetailsSection>

              <SendTestEmailSection>
                <FlexContainer justify="space-between" align="center">
                  <FlexContainer direction="column" align="flex-start">
                    <SectionTitle>
                      {t(
                        'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.title',
                      )}
                    </SectionTitle>
                    <SectionDescription>
                      {t(
                        'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.description',
                      )}
                    </SectionDescription>
                    <HelpSection>
                      <HelpTitle>
                        {t(
                          'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.help.title',
                        )}
                      </HelpTitle>
                      <HelpText>
                        {t(
                          'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.help.text',
                        )}
                      </HelpText>
                    </HelpSection>
                  </FlexContainer>
                  <div
                    id="send-test-email-btn"
                    style={{ whiteSpace: 'nowrap' }}
                  >
                    <AppButton
                      label={t(
                        'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.title',
                      )}
                      type="outlined"
                      state={
                        jobId ? LoadingStatuses.LOADING : LoadingStatuses.IDLE
                      }
                      isDisabled={!whitelistingCompleted}
                      onClick={() => setShowSendTestEmail(true)}
                    />
                  </div>
                  {!whitelistingCompleted && (
                    <Tooltip
                      target="#send-test-email-btn"
                      content={t(
                        'phishing.simulations.senderProfiles.whitelisting.sendTestEmail.disabledTooltip',
                      )}
                      position="left"
                    />
                  )}
                </FlexContainer>
              </SendTestEmailSection>

              <hr />

              <VerifySection>
                <ToggleContainer>
                  <SectionTitle>
                    {t(
                      'phishing.simulations.senderProfiles.whitelisting.verify.title',
                    )}
                  </SectionTitle>
                  <ToggleItem>
                    <ToggleSwitch
                      isChecked={whitelistingCompleted}
                      onClick={toggleWhitelisting}
                      role="switch"
                      aria-checked={whitelistingCompleted}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          toggleWhitelisting();
                        }
                      }}
                    />
                    <ToggleLabel onClick={toggleWhitelisting}>
                      {t(
                        'phishing.simulations.senderProfiles.whitelisting.verify.whitelistingCompleted',
                      )}
                    </ToggleLabel>
                  </ToggleItem>
                  <ToggleItem>
                    <ToggleSwitch
                      isChecked={testEmailReceived}
                      onClick={toggleTestEmail}
                      role="switch"
                      aria-checked={testEmailReceived}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === ' ' || e.key === 'Enter') {
                          e.preventDefault();
                          toggleTestEmail();
                        }
                      }}
                    />
                    <ToggleLabel onClick={toggleTestEmail}>
                      {t(
                        'phishing.simulations.senderProfiles.whitelisting.verify.testEmailReceived',
                      )}
                    </ToggleLabel>
                  </ToggleItem>
                </ToggleContainer>
                <AppButton
                  label={t(
                    'phishing.simulations.senderProfiles.whitelisting.confirm',
                  )}
                  onClick={handleConfirmWhitelisting}
                  isDisabled={!whitelistingCompleted || !testEmailReceived}
                />
              </VerifySection>
            </>
          )}
        </ModalContent>
      </StyledDialog>

      <SendTestEmailModal
        email={currentUser?.email as string}
        visible={!!showSendTestEmail}
        onSubmit={handleSendTestEmail}
        onHide={() => setShowSendTestEmail(false)}
        extended={false}
        withName
      />
    </>
  );
};
